// ===================================
// ENHANCED ACTIONS API SERVICE - src/services/actionsApi.ts
// ===================================
import api from './api';
import {
  Action,
  ActionField,
  CreateActionRequest,
  UpdateActionRequest,
  PaginatedResponse,
  ListActionsParams,
  SalesforcePicklistRequest,
  SalesforcePicklistResponse,
  FlowValidationRequest,
  FlowValidationResponse
} from '../types';

export class ActionsApi {
  
  // ===================================
  // ACTIONS CRUD OPERATIONS
  // ===================================

  /**
   * Get actions for a specific topic
   */
  static async getTopicActions(params: ListActionsParams): Promise<PaginatedResponse<Action>> {
    try {
      const { topic_id, ...queryParams } = params;
      const searchParams = new URLSearchParams();
      
      if (queryParams.q) searchParams.append('q', queryParams.q);
      if (queryParams.page) searchParams.append('page', queryParams.page.toString());
      if (queryParams.limit) searchParams.append('limit', queryParams.limit.toString());
      if (queryParams.sort_by) searchParams.append('sort_by', queryParams.sort_by);
      if (queryParams.sort_order) searchParams.append('sort_order', queryParams.sort_order);

      const response = await api.get(`/topics/${topic_id}/actions?${searchParams.toString()}`);
      
      // Normalize all actions in the response
      const normalizedItems = response.data.items.map((action: Action) => this.normalizeAction(action));
      
      return {
        ...response.data,
        items: normalizedItems
      };
    } catch (error) {
      console.error('Failed to get topic actions:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Get single action by ID
   */
  static async getAction(topicId: number, actionId: number): Promise<Action> {
    try {
      const response = await api.get(`/topics/${topicId}/actions/${actionId}`);
      const action = response.data.data || response.data;
      return this.normalizeAction(action);
    } catch (error) {
      console.error('Failed to get action:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Create action under a topic with enhanced validation
   */
  static async createAction(data: CreateActionRequest): Promise<Action> {
    try {
      // Clean and validate data before sending
      const cleanedData = this.cleanActionData(data);
      
      console.log('Creating action with cleaned data:', {
        action_name: cleanedData.action_name,
        flow_name: cleanedData.flow_name,
        input_params_count: cleanedData.input_params.length,
        output_params_count: cleanedData.output_params.length,
        required_fields: cleanedData.required_fields
      });

      const { topic_id, ...actionData } = cleanedData;
      const response = await api.post(`/topics/${topic_id}/actions`, actionData);
      const action = response.data.data;
      return this.normalizeAction(action);
    } catch (error) {
      console.error('Failed to create action:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Update action
   */
  static async updateAction(topicId: number, actionId: number, data: UpdateActionRequest): Promise<Action> {
    try {
      // Clean and validate data before sending
      const cleanedData = this.cleanUpdateActionData(data);
      
      console.log('Updating action with cleaned data:', {
        action_id: actionId,
        input_params_count: cleanedData.input_params?.length,
        output_params_count: cleanedData.output_params?.length,
        required_fields: cleanedData.required_fields
      });

      const response = await api.put(`/topics/${topicId}/actions/${actionId}`, cleanedData);
      const action = response.data.data;
      return this.normalizeAction(action);
    } catch (error) {
      console.error('Failed to update action:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Delete action
   */
  static async deleteAction(topicId: number, actionId: number): Promise<void> {
    try {
      await api.delete(`/topics/${topicId}/actions/${actionId}`);
    } catch (error) {
      console.error('Failed to delete action:', error);
      throw this.handleApiError(error);
    }
  }

  // ===================================
  // SALESFORCE INTEGRATION
  // ===================================

  /**
   * Validate Salesforce flow existence
   */
  static async validateFlow(data: FlowValidationRequest): Promise<FlowValidationResponse> {
    try {
      const response = await api.post('/salesforce/validate-flow', data);
      return response.data;
    } catch (error) {
      console.error('Failed to validate flow:', error);
      throw this.handleApiError(error);
    }
  }

  /**
   * Get Salesforce picklist values
   */
  static async getPicklistValues(data: SalesforcePicklistRequest): Promise<SalesforcePicklistResponse> {
    try {
      const response = await api.post('/salesforce/picklist', data);
      return response.data;
    } catch (error) {
      console.error('Failed to get picklist values:', error);
      throw this.handleApiError(error);
    }
  }

  // ===================================
  // DATA CLEANING AND VALIDATION
  // ===================================

  /**
   * Clean action field to prevent JSON parsing errors
   */
  static cleanActionField(field: ActionField): ActionField {
    const cleaned: ActionField = {
      name: field.name?.trim() || '',
      type: field.type || 'string',
      required: Boolean(field.required),
      sample_value: field.sample_value?.trim() || undefined
    };

    // Only include constraints if they have valid data
    if (field.constraints && typeof field.constraints === 'object') {
      const constraints: any = {};
      let hasValidConstraints = false;

      if (field.constraints.min !== undefined && field.constraints.min !== null && !isNaN(Number(field.constraints.min))) {
        constraints.min = Number(field.constraints.min);
        hasValidConstraints = true;
      }

      if (field.constraints.max !== undefined && field.constraints.max !== null && !isNaN(Number(field.constraints.max))) {
        constraints.max = Number(field.constraints.max);
        hasValidConstraints = true;
      }

      if (Array.isArray(field.constraints.values) && field.constraints.values.length > 0) {
        constraints.values = field.constraints.values
          .map(v => String(v).trim())
          .filter(v => v.length > 0);
        if (constraints.values.length > 0) {
          hasValidConstraints = true;
        }
      }

      if (hasValidConstraints) {
        cleaned.constraints = constraints;
      }
    }

    return cleaned;
  }

  /**
   * Clean action data before API call
   */
  static cleanActionData(data: CreateActionRequest): CreateActionRequest {
    // Clean basic fields
    const cleaned: CreateActionRequest = {
      topic_id: data.topic_id,
      action_name: data.action_name?.trim() || '',
      description: data.description?.trim() || undefined,
      flow_name: data.flow_name?.trim() || '',
      input_params: [],
      output_params: [],
      required_fields: [],
      example_use_case: data.example_use_case?.trim() || undefined,
      sample_data: data.sample_data
    };

    // Clean input parameters
    if (Array.isArray(data.input_params)) {
      cleaned.input_params = data.input_params
        .map(field => this.cleanActionField(field))
        .filter(field => field.name.length > 0); // Only keep fields with names
    }

    // Clean output parameters
    if (Array.isArray(data.output_params)) {
      cleaned.output_params = data.output_params
        .map(field => this.cleanActionField(field))
        .filter(field => field.name.length > 0); // Only keep fields with names
    }

    // Clean required fields - ensure they're all strings and exist in input params
    if (Array.isArray(data.required_fields)) {
      const inputFieldNames = new Set(cleaned.input_params.map(f => f.name));
      cleaned.required_fields = data.required_fields
        .map(name => String(name).trim())
        .filter(name => name.length > 0 && inputFieldNames.has(name));
    }

    // Validate required fields
    this.validateRequiredFields(cleaned.input_params, cleaned.required_fields);

    return cleaned;
  }

  /**
   * Clean update action data
   */
  static cleanUpdateActionData(data: UpdateActionRequest): UpdateActionRequest {
    const cleaned: UpdateActionRequest = {};

    if (data.action_name !== undefined) {
      cleaned.action_name = data.action_name?.trim() || '';
    }

    if (data.description !== undefined) {
      cleaned.description = data.description?.trim() || undefined;
    }

    if (data.flow_name !== undefined) {
      cleaned.flow_name = data.flow_name?.trim() || '';
    }

    if (data.input_params !== undefined && Array.isArray(data.input_params)) {
      cleaned.input_params = data.input_params
        .map(field => this.cleanActionField(field))
        .filter(field => field.name.length > 0);
    }

    if (data.output_params !== undefined && Array.isArray(data.output_params)) {
      cleaned.output_params = data.output_params
        .map(field => this.cleanActionField(field))
        .filter(field => field.name.length > 0);
    }

    if (data.required_fields !== undefined && Array.isArray(data.required_fields)) {
      const inputFieldNames = cleaned.input_params 
        ? new Set(cleaned.input_params.map(f => f.name))
        : new Set();
      
      cleaned.required_fields = data.required_fields
        .map(name => String(name).trim())
        .filter(name => name.length > 0 && (inputFieldNames.size === 0 || inputFieldNames.has(name)));
    }

    if (data.example_use_case !== undefined) {
      cleaned.example_use_case = data.example_use_case?.trim() || undefined;
    }

    if (data.sample_data !== undefined) {
      cleaned.sample_data = data.sample_data;
    }

    // Validate required fields if both input_params and required_fields are being updated
    if (cleaned.input_params && cleaned.required_fields) {
      this.validateRequiredFields(cleaned.input_params, cleaned.required_fields);
    }

    return cleaned;
  }

  /**
   * Validate that required fields exist in input parameters
   */
  static validateRequiredFields(inputParams: ActionField[], requiredFields: string[]): void {
    const inputFieldNames = new Set(inputParams.map(f => f.name));
    const invalidRequired = requiredFields.filter(name => !inputFieldNames.has(name));
    
    if (invalidRequired.length > 0) {
      throw new Error(`Required fields must exist in input parameters: ${invalidRequired.join(', ')}`);
    }
  }

  /**
   * Normalize action data from API response - ENHANCED VERSION
   */
  static normalizeAction(action: Action): Action {
    if (!action) {
      console.warn('Attempting to normalize null/undefined action');
      return action;
    }

    try {
      // Normalize the parameter arrays
      const normalizedInputParams = this.normalizeParamsArray(action.input_params);
      const normalizedOutputParams = this.normalizeParamsArray(action.output_params);
      
      // Normalize required fields to always be an array of strings
      let normalizedRequiredFields: string[] = [];
      if (Array.isArray(action.required_fields)) {
        normalizedRequiredFields = action.required_fields.map(field => String(field)).filter(Boolean);
      } else if (action.required_fields && typeof action.required_fields === 'object') {
        // Handle legacy object format { field1: true, field2: false }
        normalizedRequiredFields = Object.keys(action.required_fields)
          .filter(key => Boolean((action.required_fields as any)[key]));
      }

      // Use backend computed properties if available, otherwise calculate them
      const input_count = action.input_count !== undefined ? action.input_count : normalizedInputParams.length;
      const output_count = action.output_count !== undefined ? action.output_count : normalizedOutputParams.length;
      const required_count = action.required_count !== undefined ? action.required_count : normalizedRequiredFields.length;

      // Return the normalized action with all computed properties
      const normalizedAction: Action = {
        ...action,
        input_params: normalizedInputParams,
        output_params: normalizedOutputParams,
        required_fields: normalizedRequiredFields,
        // Preserve backend computed properties or use calculated ones
        input_count,
        output_count,
        required_count
      };

      console.log('Normalized action:', {
        id: normalizedAction.id,
        action_name: normalizedAction.action_name,
        input_count,
        output_count,
        required_count,
        source: action.input_count !== undefined ? 'backend' : 'calculated'
      });

      return normalizedAction;
    } catch (error) {
      console.error('Error normalizing action:', error, action);
      // Return a safe fallback with backend values if available
      return {
        ...action,
        input_params: [],
        output_params: [],
        required_fields: [],
        input_count: action.input_count || 0,
        output_count: action.output_count || 0,
        required_count: action.required_count || 0
      };
    }
  }

  /**
   * Normalize parameters array (handle both new and legacy formats) - ENHANCED VERSION
   */
  static normalizeParamsArray(params: any): ActionField[] {
    if (!params) {
      return [];
    }
    
    try {
      // If it's already an array of ActionField objects, validate and return
      if (Array.isArray(params)) {
        // Check if it's already in the correct ActionField format
        if (params.length > 0 && typeof params[0] === 'object' && 'name' in params[0]) {
          return params.map(field => ({
            name: String(field.name || ''),
            type: field.type || 'string',
            required: Boolean(field.required),
            sample_value: field.sample_value ? String(field.sample_value) : undefined,
            constraints: field.constraints
          })).filter(field => field.name.length > 0);
        }
        
        // If it's an array of strings (legacy format), convert to ActionField format
        if (params.length > 0 && typeof params[0] === 'string') {
          return params.map((name: string) => ({
            name: String(name),
            type: 'string' as const,
            required: false,
            sample_value: undefined
          })).filter(field => field.name.length > 0);
        }
        
        // If it's an empty array or array of invalid items, return empty
        return [];
      }
      
      // If it's an object with 'fields' property (another legacy format)
      if (params && typeof params === 'object' && Array.isArray(params.fields)) {
        return this.normalizeParamsArray(params.fields);
      }
      
      // If it's an object (legacy format { fieldName: value }), convert keys to ActionField format
      if (params && typeof params === 'object' && !Array.isArray(params)) {
        return Object.keys(params).map(name => ({
          name,
          type: 'string' as const,
          required: Boolean(params[name]),
          sample_value: undefined
        })).filter(field => field.name.length > 0);
      }
      
      console.warn('Unknown params format:', typeof params, params);
      return [];
      
    } catch (error) {
      console.error('Error normalizing params array:', error, params);
      return [];
    }
  }

  // ===================================
  // ERROR HANDLING
  // ===================================

  /**
   * Handle API errors consistently with detailed messages
   */
  static handleApiError(error: any): Error {
    let errorMessage = 'An unexpected error occurred';
    let errorDetails = '';

    if (error.response?.data) {
      const data = error.response.data;
      
      // Handle validation errors
      if (data.details && Array.isArray(data.details)) {
        const validationErrors = data.details.map((detail: any) => 
          `${detail.path?.join('.')} ${detail.msg}`
        ).join(', ');
        errorMessage = `Validation Error: ${validationErrors}`;
      }
      // Handle backend error messages
      else if (data.error) {
        errorMessage = data.error;
      }
      // Handle error messages
      else if (data.message) {
        errorMessage = data.message;
      }
      
      // Add status code context
      if (error.response.status) {
        errorDetails = ` (Status: ${error.response.status})`;
      }
    }
    // Handle network errors
    else if (error.request) {
      errorMessage = 'Network error: Unable to connect to server';
    }
    // Handle other errors
    else if (error.message) {
      errorMessage = error.message;
    }

    const finalError = new Error(errorMessage + errorDetails);
    
    // Attach original error for debugging
    (finalError as any).originalError = error;
    
    return finalError;
  }

  // ===================================
  // UTILITY METHODS
  // ===================================

  /**
   * Get action statistics
   */
  static getActionStats(actions: Action[]) {
    const normalizedActions = actions.map(action => this.normalizeAction(action));
    
    return {
      total: normalizedActions.length,
      withRequiredFields: normalizedActions.filter(a => a.required_fields.length > 0).length,
      withSampleData: normalizedActions.filter(a => a.sample_data).length,
      byType: normalizedActions.reduce((acc, action) => {
        const inputTypes = action.input_params
          .map((p: ActionField) => p.type)
          .filter(Boolean);
        
        inputTypes.forEach((type: string) => {
          acc[type] = (acc[type] || 0) + 1;
        });
        
        return acc;
      }, {} as Record<string, number>)
    };
  }

  /**
   * Export actions for CSV
   */
  static exportActionsToCSV(actions: Action[]): string {
    const headers = [
      'Action Name',
      'Description', 
      'Flow Name',
      'Input Fields',
      'Output Fields',
      'Required Fields',
      'Example Use Case',
      'Created At'
    ];

    const rows = actions.map(action => {
      const normalized = this.normalizeAction(action);
      return [
        action.action_name,
        action.description || '',
        action.flow_name,
        normalized.input_params.map((p: ActionField) => `${p.name}:${p.type}`).join(';'),
        normalized.output_params.map((p: ActionField) => `${p.name}:${p.type}`).join(';'),
        normalized.required_fields.join(';'),
        action.example_use_case || '',
        action.created_at
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  }
}

export default ActionsApi;